import {CONTROLROOM_TABLE_HEADER_HEIGHT} from "../ui/table/ControlRoomTable";
import {ValidationResult} from "../ui/validation/ProductValidation";
import {ADD_DATA_INITIAL_FOLDER_KEY, localRepository, WMS_BROWSER_CACHE_VERSION} from "../LocalRepository";
import {Api} from "../../api/InternalApi";

/**
 * Returns a property value as a number or null if property does not exist
 * @param aObj an objects that holds a property, e.g. ownProps.params
 * @param propertyName the property name
 */
const getNumberProperty = (aObj: any, propertyName: string): number => {
    const value = getProperty(aObj, propertyName);
    return value != null ? Number.parseInt(value, 10) : null;
};

/**
 * Returns a property value or null if property does not exist
 * @param aObj an objects that holds a property, e.g. ownProps.params
 * @param propertyName the property name
 */
const getProperty = (aObj: any, propertyName: string): any => {
  if (aObj?.[propertyName]) {
        return aObj[propertyName];
    }
    return null;
};

// either passed as own properties or as a router parameter
/**
 * Returns a property value from ownProps that may be passes as a react-router-dom parameter.
 * @param ownProps
 * @param propertyName
 */
const getNumberFromOwnProps = (ownProps: any, propertyName: string): number => {
  return ownProps?.[propertyName] ?
         getNumberProperty(ownProps, propertyName) :
         getNumberProperty(ownProps.match.params, propertyName);
}

// either passed as own properties or as a router parameter
/**
 * Returns a property value from ownProps that may be passes as a react-router-dom parameter.
 * @param ownProps
 * @param propertyName
 */
const getParameterFromOwnProps = (ownProps: any, propertyName: string): string => {
  return ownProps?.[propertyName] ?
         getProperty(ownProps, propertyName) :
         getProperty(ownProps.match.params, propertyName);
}

/**
 * Returns concatenated error and warning messages of the given validation.
 * @param {ValidationResult} API validation
 * @returns {string} concatenated error message
 */
const getConcatenatedValidationMessages = (validation: ValidationResult): string => {
    let message = "";
    validation.errorMessages.forEach((aMessage) => message += aMessage + " ");
    validation.warningMessages.forEach((aMessage) => message += aMessage);
    return message;
};

export function calculateDimensionBasedOnRowsBeforeScroll(rowsBeforeScroll: number, rowHeight: number): (parent: HTMLElement) => {} {
    return (parent: HTMLElement) => {
        return {
            width: getFirstParentWithWidth(parent).clientWidth,
            height: rowsBeforeScroll * rowHeight + CONTROLROOM_TABLE_HEADER_HEIGHT + 2,
        };
    };
}

// When a ControlRoomTable component is used within a Tab and that Tab is not the default active tab, it won't have
// a clientWidth (activating a tab gives the active one width and hides the others by setting width to 0 for previously
// active).  If we walk up the parent tree, we will get the TabContainer which will have a usable width.
export function getFirstParentWithWidth(child: HTMLElement): HTMLElement {
    let newParent: HTMLElement = child;
  while (newParent?.clientWidth === 0) {
    newParent = newParent?.parentElement;
    if (!newParent) {
      break;
    }
    }
  return newParent ?? child;
}

function isEmpty(obj: any): boolean {
    for (const key in obj) {
        if (obj.hasOwnProperty(key))
            return false;
    }
    return true;
}

function getErrorMessage(error: any): string {
  if (error?.response?.data?.details) {
    return error.response.data.details;
  }
  return error.message;
}

function reactComponentOnChange(this: React.Component, event: any) {
    const {name} = event.target;
    let {value} = event.target;
    value = event.target.type === 'checkbox' ? event.target.checked : value;
    if (event.target.type === 'number' && value?.length > 0) {
        value = +value;
    }

    this.setState({[name]: value} as any);
}

function loadCssfile(filename) {
    const fileref = document.createElement("link");
    fileref.setAttribute("rel", "stylesheet");
    fileref.setAttribute("type", "text/css");
    fileref.setAttribute("href", filename);
    document.getElementsByTagName("head")[0].appendChild(fileref);
}

function removeMainStyles() {
    const children = document.getElementsByTagName("head")[0].children;
    for (const child of children) {
        if (child.tagName.toLocaleLowerCase() === "link" && /.*style\.css/.test(child.getAttribute("href"))) {
            child.remove();
        }
    }
}

function generateUrl(relativePath: string) {
    if (`${window.location.pathname}`.endsWith('index.html')) {
        return `${window.location.pathname.replace("index.html", "")}#${relativePath}`;
    } else if (`${window.location.pathname}`.endsWith('/')) {
        return `${window.location.pathname}#${relativePath}`;
    } else {
        return `${window.location.pathname}/#${relativePath}`;
    }
}

function stringToXml(text: string, type: DOMParserSupportedType = "application/xml") {
    const parser = new DOMParser();
    return parser.parseFromString(text, type);
}

/**
 * Performs a deep equality check between two values, handling circular references.
 * @param value1 The first value to compare.
 * @param value2 The second value to compare.
 * @param visited A WeakMap to track visited objects for circular reference handling.
 * @returns True if the values are deeply equal, false otherwise.
 */
function deepEquals(value1: any, value2: any, visited = new WeakMap()): boolean {
  // Use Object.is for primitive comparison
  if (Object.is(value1, value2)) {
    return true;
  }

  // Handle null or undefined values
  if (value1 == null || value2 == null) {
    return value1 === value2;
  }

  // Handle non-object types (e.g., primitives, functions)
  if (typeof value1 !== "object" || typeof value2 !== "object") {
    return false;
  }

  // Handle circular references
  if (visited.has(value1)) {
    return visited.get(value1) === value2;
  }
  visited.set(value1, value2);

  // Handle arrays
  if (Array.isArray(value1) && Array.isArray(value2)) {
    if (value1.length !== value2.length) {
      return false;
    }
    for (let i = 0; i < value1.length; i++) {
      if (!deepEquals(value1[i], value2[i], visited)) {
        return false;
      }
    }
    return true;
  }

  // Handle objects
  const keys1 = Object.keys(value1);
  const keys2 = Object.keys(value2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    if (!keys2.includes(key) || !deepEquals(value1[key], value2[key], visited)) {
      return false;
    }
  }

  return true;
}

function invalidateWMSCache(): void {
  const cacheVersion = (parseInt(localRepository.getParameter(WMS_BROWSER_CACHE_VERSION) || "0",
                           10) + 1) %
                       Number.MAX_SAFE_INTEGER;
  localRepository.setParameter(WMS_BROWSER_CACHE_VERSION, cacheVersion.toString());
}

async function getInitialPath(api: Api): Promise<string> {
  const initialPath: string = localRepository.getParameter(ADD_DATA_INITIAL_FOLDER_KEY);
  //remove extra \ characters also remove quotes from the start and end of the retrieved path
  let tidyInitialPath = initialPath?.replace(/\\\\*/g, '\\').replace(/"/g, "");
  //if the path starts with a \, we add another one back in for unc paths
  tidyInitialPath = tidyInitialPath?.startsWith("\\") ? "\\" + tidyInitialPath : tidyInitialPath
  if (tidyInitialPath) {
    return tidyInitialPath;
  }
  return await api.getFusionDatastoreDirectory();
}

export {
  isEmpty,
  getNumberProperty,
  getNumberFromOwnProps,
  getParameterFromOwnProps,
  getConcatenatedValidationMessages,
  getErrorMessage,
  reactComponentOnChange,
  loadCssfile,
  removeMainStyles,
  generateUrl,
  stringToXml,
  deepEquals,
  invalidateWMSCache,
  getInitialPath
};